// client/src/components/pages/LexiStar/StatusBoard/StatusBoard.tsx (converted from .jsx, ST-00207)
//
// LexiStar status board (ST-00188, M2 T6-S04; LS-09 §5.3, FR-STAT-001…010).
// Route /lexistar/status-board, page permission lexistar.trips.view.
//
// One row per visible asset from GET /api/lexistar/status-board: the two-axis
// status (motion · context), the active trip's progress on its route with the
// route-average sense check, fuel in litres against the allowance (provenance,
// burn index, over-consumption tag), the run chip, time in status and data age.
//
// Views: control room (default since 2026-10-19, see below), table (?view=table), kanban by
// operational context, run-grouped (?view=run on the API). Summary tiles filter. View, filter
// and search live in the URL
// (LS-09 principle 7). Live: `lexistar:event` on the tenant room highlights the
// row and triggers a debounced refetch; a 30 s poll covers a dropped socket.
//
// Manual override (lexistar.trips.manage) → POST /trips/:id:overrideStage.
// The run chip opens the truck's run with *Next load from here* at its end
// (lexistar.trips.manage).
// Bulk: CSV export of the selection; driver messaging is a placeholder until
// the conversation engine (M3).
//
// ST-00207 (m4b-contracts §5, phone pass; converted to TypeScript in the same change): under
// 640 px or on a touch screen the board is a phone layout — one card per truck with a chevron
// that opens its trip (StatusBoardPhone.tsx), the status tiles and the view (List / By status /
// Runs) in one bottom sheet behind a "Show" field, the search above the cards, "By status" as
// headed groups and a run as a top-to-bottom strip (no sideways scrolling), Override and the run
// chip as 44 px taps, the override dialog and the run drawer with 44 px targets. Selecting rows
// for the CSV export stays on a computer. The desktop board is unchanged.
//
// ST-00253 (the control room): a fourth view, first in the switcher — ?view=control, the
// colour-coded groups in the order a load moves (ControlRoom.tsx, controlRoom.ts), and its wall
// display (&wall=1, dark unless &theme=light). A chip opens the trip (a truck without one: its
// row in the table); a group or sub-group opens the table filtered to it (f=room.<group>[.<sub>],
// the same placement rules, so the counts agree). The scope — Fleet / Loaders (cls), site,
// route, customer (cust) — lives in the URL and narrows every view; the table and Kanban show it
// as a chip to clear.
//
// The board opens on the control room (founder decision 2026-10-19): no ?view (or an unknown
// one) is the control room, on a computer and on a phone, and choosing it clears the param;
// the table, Kanban and Runs are ?view=table / kanban / run. Every link that wants the table —
// the control room's own drill-downs (f=room.…, q=<registration>), the dashboard KPI drills,
// notification links, help — names view=table.

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaTruckMoving,
  FaSearch,
  FaSyncAlt,
  FaColumns,
  FaFileExport,
  FaCommentDots,
  FaTable,
  FaThLarge,
  FaStream,
  FaTimes,
  FaTv,
} from 'react-icons/fa';
import { SegmentedControl, useAuth } from '../../../../types/commonComponents';
import { getStatusBoard, pickList } from '../../../../services/lexistarStatusBoard';
import useLexiStarEvents from './useLexiStarEvents';
import StatusTable from './StatusTable';
import KanbanBoard from './KanbanBoard';
import RunStripJs from './RunStrip';
import RunDrawerJs from './RunDrawer';
import OverrideDialog from './OverrideDialog';
import NextLoadPanel, { type NextLoadSummary } from '../PlanDispatch/NextLoadPanel';
import { COLUMNS, DEFAULT_VISIBLE, rowRegistration, rowDriver, type StatusBoardColumn, type ColumnCtx } from './columns';
import {
  CONTEXT_GROUPS as CONTEXT_GROUPS_JS,
  isOffline,
  isDeviating,
} from './statusLanguage';
import { ASSET_STATUS_CLASSES, type AssetStatusKey } from '../../../map/assetStatus';
import {
  PageHeader,
  ErrorBanner,
  NoticeBanner,
  LoadingState,
  EmptyState,
  NoPermissionPage,
  ConnectionIndicator,
  cardClasses,
  type RunSummary,
} from './atoms';
import { PhoneStatusCards, PhoneStatusGroups } from './StatusBoardPhone';
import ControlRoom, { ScopeChip, type OnTruckAction, type WallTheme } from './ControlRoom';
import QuickBooking from '../Maintenance/QuickBooking';
import type { BookingMode } from '../Maintenance/maintenanceLogic';
import {
  matchesScope,
  roomFilterId,
  roomFilterLabel,
  roomFilterMatcher,
  scopeFromParams,
  scopeIsSet,
  scopeOptions,
  scopeToParams,
  EMPTY_SCOPE,
  type ControlGroupId,
  type ControlScope,
} from './controlRoom';
import PhoneFilterSheet from '../../../common/PhoneFilterSheet';
import { usePhoneLayout } from '../../../common/phoneLayout';
import type { StatusBoardRow } from '../../../../types/lexistar-status-board';

/** A summary tile (CONTEXT_GROUPS in statusLanguage.js, plus the overlay tiles below). */
interface Tile {
  id: string;
  label: string;
  /** The tile's status key (map/assetStatus): the same colour as its trucks everywhere. */
  tone?: AssetStatusKey;
  glyph?: string;
}
interface ContextGroup extends Tile {
  contexts: string[];
  tone: AssetStatusKey;
}
const CONTEXT_GROUPS = CONTEXT_GROUPS_JS as ContextGroup[];

/** A run as the run-grouped board and the run drawer receive it (GET /runs/:id/summary). */
interface RunGroup extends NextLoadSummary {
  run: NextLoadSummary['run'] & {
    registration?: string | null;
    asset_key?: string | null;
    driver_name?: string | null;
    state?: string | null;
  };
}

// Typed views of the JS pieces (RunStrip.jsx, RunDrawer.jsx).
const RunStrip = RunStripJs as unknown as ComponentType<{ summary: RunGroup; children?: ReactNode }>;
const RunDrawer = RunDrawerJs as unknown as ComponentType<{
  runId?: string;
  isOpen: boolean;
  onClose: () => void;
  renderNextLoad?: (summary: RunGroup, reload: () => void) => ReactNode;
  title?: string;
}>;

type View = 'control' | 'table' | 'kanban' | 'run';

/** The view the board opens on when the address names none (founder decision 2026-10-19). */
const DEFAULT_VIEW: View = 'control';

const POLL_MS = 30000;
const LIVE_REFETCH_DEBOUNCE_MS = 1500;
const HIGHLIGHT_MS = 4000;
const TICK_MS = 5000;
const COLUMNS_STORAGE_KEY = 'lexistar.statusBoard.columns';

const VIEWS: Array<{ value: View; label: string; icon: typeof FaTable }> = [
  { value: 'control', label: 'Control room', icon: FaTv },
  { value: 'table', label: 'Table', icon: FaTable },
  { value: 'kanban', label: 'Kanban', icon: FaThLarge },
  { value: 'run', label: 'Runs', icon: FaStream },
];

/** The views as a phone names them (the table is a list of cards there). */
const PHONE_VIEWS: Array<{ value: View; label: string; hint: string }> = [
  { value: 'control', label: 'Control room', hint: 'Every group with its count, then its trucks' },
  { value: 'table', label: 'List', hint: 'One card per truck' },
  { value: 'kanban', label: 'By status', hint: 'The cards grouped by what the trucks are doing' },
  { value: 'run', label: 'Runs', hint: "Each truck's day, trip by trip" },
];

const EXTRA_TILES: Array<Tile & { match: (r: StatusBoardRow) => boolean }> = [
  { id: 'deviating', label: 'Deviating', glyph: '⤴', match: (r) => isDeviating(r.status) },
  { id: 'offline', label: 'Offline', glyph: '⌀', match: (r) => isOffline(r.status) },
];

export const tileMatcher = (tileId: string): ((r: StatusBoardRow) => boolean) => {
  // ST-00253: a control-room group or sub-group (f=room.<group>[.<sub>]).
  const room = roomFilterMatcher(tileId);
  if (room) return room;
  const extra = EXTRA_TILES.find((t) => t.id === tileId);
  if (extra) return extra.match;
  const group = CONTEXT_GROUPS.find((g) => g.id === tileId);
  if (group) return (r) => group.contexts.includes(r.status?.context || 'UNALLOCATED');
  return () => true;
};

const readColumns = (): string[] => {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(ids) && ids.length)
      return ids.filter((id): id is string => COLUMNS.some((c) => c.id === id));
  } catch {
    /* storage unavailable — defaults */
  }
  return DEFAULT_VISIBLE;
};

const writeColumns = (ids: string[]) => {
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
};

const matchesSearch = (r: StatusBoardRow, q: string): boolean => {
  if (!q) return true;
  const hay = [
    rowRegistration(r),
    r.fleet_no,
    rowDriver(r),
    r.trip?.reference,
    r.trip?.from_name,
    r.trip?.to_name,
    r.trip?.route?.name,
    r.position?.place,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q.toLowerCase());
};

const csvEscape = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function buildCsv(rows: StatusBoardRow[], columns: StatusBoardColumn[], ctx: ColumnCtx): string {
  const header = columns.map((c) => csvEscape(c.label)).join(',');
  const lines = rows.map((r) =>
    columns.map((c) => csvEscape(c.csv ? c.csv(r, ctx) : '')).join(',')
  );
  return [header, ...lines].join('\n');
}

type ErrorLike = { response?: { data?: { error?: string } } } | null;

const StatusBoard = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.trips.view');
  const canOverride = hasPermission('lexistar.trips.manage');
  const isManager = hasPermission('lexistar.fuel.manage');
  // ST-00249: Report breakdown / Book maintenance from a control-room chip.
  const canMaintain = hasPermission('lexistar.maintenance.manage');
  const phone = usePhoneLayout();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const view: View = VIEWS.some((v) => v.value === searchParams.get('view'))
    ? (searchParams.get('view') as View)
    : DEFAULT_VIEW;
  /** The view param for a view: the default leaves the address without one. */
  const viewParam = (v: View): string => (v === DEFAULT_VIEW ? '' : v);
  const tile = searchParams.get('f') || '';
  const query = searchParams.get('q') || '';
  const wall = view === 'control' && searchParams.get('wall') === '1';
  const wallTheme: WallTheme = searchParams.get('theme') === 'light' ? 'light' : 'dark';
  const scopeKey = ['cls', 'site', 'route', 'cust'].map((k) => searchParams.get(k) || '').join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the four scope params only
  const scope: ControlScope = useMemo(() => scopeFromParams(searchParams), [scopeKey]);

  const [rows, setRows] = useState<StatusBoardRow[]>([]);
  const [runs, setRuns] = useState<RunGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [visible, setVisible] = useState<string[]>(readColumns);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [sort, setSort] = useState<{ id: string; dir: 'asc' | 'desc' }>({ id: 'asset', dir: 'asc' });
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [highlighted, setHighlighted] = useState<Set<string>>(() => new Set());
  const [overrideRow, setOverrideRow] = useState<StatusBoardRow | null>(null);
  const [runDrawer, setRunDrawer] = useState<RunSummary | null>(null);
  const [quick, setQuick] = useState<{ mode: BookingMode; assetId: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // ST-00253: when the board last loaded (the control room's data-age warning).
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const setParams = useCallback(
    (changes: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const setParam = (key: string, value: string) => setParams({ [key]: value });

  const fetchData = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!canView) return;
      try {
        if (!quiet) setLoading(true);
        if (view === 'run') {
          const res = await getStatusBoard({ view: 'run' });
          setRuns(pickList(res, 'runs') as RunGroup[]);
        } else {
          const res = await getStatusBoard();
          setRows(pickList(res, 'rows', 'assets') as StatusBoardRow[]);
        }
        setFetchedAt(Date.now());
        setError(null);
      } catch (err) {
        if (!quiet) setError((err as ErrorLike)?.response?.data?.error || 'Failed to load the status board.');
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [canView, view]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!canView) return undefined;
    const poll = setInterval(() => fetchData({ quiet: true }), POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
      clearTimeout(refetchTimer.current);
    };
  }, [canView, fetchData]);

  const connection = useLexiStarEvents({
    enabled: canView,
    onEvent: (evt: { asset_key?: string | null }) => {
      const key = evt.asset_key;
      if (key) {
        setHighlighted((prev) => new Set(prev).add(key));
        setTimeout(() => {
          setHighlighted((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }, HIGHLIGHT_MS);
      }
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => fetchData({ quiet: true }), LIVE_REFETCH_DEBOUNCE_MS);
    },
  });

  // ST-00253: the scope (Fleet / Loaders, site, route, customer) narrows every view.
  const options = useMemo(() => scopeOptions(rows), [rows]);
  const scoped = useMemo(
    () => (scopeIsSet(scope) ? rows.filter((r) => matchesScope(r, scope)) : rows),
    [rows, scope]
  );
  const searched = useMemo(() => scoped.filter((r) => matchesSearch(r, query)), [scoped, query]);

  const tileCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    [...CONTEXT_GROUPS, ...EXTRA_TILES].forEach((t) => {
      counts[t.id] = searched.filter(tileMatcher(t.id)).length;
    });
    // A control-room filter (f=room.…) counts too.
    if (tile && counts[tile] === undefined) counts[tile] = searched.filter(tileMatcher(tile)).length;
    return counts;
  }, [searched, tile]);

  const filtered = useMemo(
    () => (tile ? searched.filter(tileMatcher(tile)) : searched),
    [searched, tile]
  );

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.id === sort.id) || COLUMNS[0]!;
    const ctx = { now };
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = col.sortValue(a, ctx);
      const vb = col.sortValue(b, ctx);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filtered, sort, now]);

  const availableColumns = COLUMNS.filter((c) => !c.manager || isManager);
  const visibleIds = visible.filter((id) => availableColumns.some((c) => c.id === id));

  const toggleColumn = (id: string) => {
    const next = visible.includes(id) ? visible.filter((v) => v !== id) : [...visible, id];
    const ordered = COLUMNS.map((c) => c.id).filter((cid) => next.includes(cid));
    setVisible(ordered);
    writeColumns(ordered);
  };

  const onSort = (id: string) =>
    setSort((s) =>
      s.id === id ? { id, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { id, dir: 'asc' }
    );

  const toggleRow = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAll = (on: boolean) => setSelected(on ? new Set(sorted.map((r) => r.asset_key)) : new Set());

  const exportSelected = () => {
    const chosen = sorted.filter((r) => selected.has(r.asset_key));
    const cols = COLUMNS.filter((c) => visibleIds.includes(c.id));
    const csv = buildCsv(chosen, cols, { now });
    try {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lexistar-status-board-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* no download support (tests / old browsers) */
    }
    setNotice(`Exported ${chosen.length} row${chosen.length === 1 ? '' : 's'} to CSV.`);
  };

  const openTrip = (r: StatusBoardRow) => {
    if (r.trip) navigate(`/lexistar/trips/${r.trip.id}`);
  };

  // ST-00253: the control room's clicks. A chip opens the trip, else the truck's row in the
  // table; a group (or sub-group) opens the table filtered to exactly its trucks. The table is
  // named (view=table): the board's default is the control room.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;
  const openControlRow = useCallback(
    (r: StatusBoardRow) => {
      if (r.trip) navigate(`/lexistar/trips/${r.trip.id}`);
      else setParamsRef.current({ view: 'table', wall: '', theme: '', f: '', q: rowRegistration(r) });
    },
    [navigate]
  );
  const openControlGroup = useCallback((group: ControlGroupId, subKey?: string | null) => {
    setParamsRef.current({ view: 'table', wall: '', theme: '', q: '', f: roomFilterId(group, subKey) });
  }, []);
  const setScope = (next: ControlScope) => setParams(scopeToParams(next));
  const enterWall = () => {
    setParams({ view: 'control', wall: '1' });
    try {
      const el = document.documentElement as HTMLElement & { requestFullscreen?: () => Promise<void> };
      el.requestFullscreen?.().catch(() => {});
    } catch {
      /* no fullscreen: the wall display still fills the window */
    }
  };
  const exitWall = useCallback(() => {
    setParamsRef.current({ wall: '', theme: '' });
    try {
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    } catch {
      /* ignore */
    }
  }, []);

  const tiles = [...CONTEXT_GROUPS, ...EXTRA_TILES].filter(
    (t) => (tileCounts[t.id] ?? 0) > 0 || t.id === tile
  );
  const roomLabel = tile ? roomFilterLabel(tile, rows, now) : null;
  const tileLabel = tile
    ? roomLabel || [...CONTEXT_GROUPS, ...EXTRA_TILES].find((t) => t.id === tile)?.label || tile
    : '';
  const swatch = (t: Tile): ReactNode =>
    t.tone ? (
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${ASSET_STATUS_CLASSES[t.tone].dot}`} aria-hidden="true" />
    ) : (
      <span aria-hidden="true">{t.glyph}</span>
    );

  // The scope on a phone: one group per filter in the same sheet (ST-00253).
  const scopeGroups =
    view === 'run'
      ? []
      : [
          {
            id: 'cls',
            legend: 'Trucks or loaders',
            value: scope.cls === 'all' ? '' : scope.cls,
            defaultValue: '',
            choices: [
              { value: '', label: 'All' },
              { value: 'fleet', label: 'Fleet' },
              { value: 'loaders', label: 'Loaders', count: options.loaders },
            ],
          },
          ...(options.sites.length
            ? [
                {
                  id: 'site',
                  legend: 'Site',
                  value: scope.site,
                  defaultValue: '',
                  choices: [{ value: '', label: 'All sites' }, ...options.sites],
                },
              ]
            : []),
          ...(options.routes.length
            ? [
                {
                  id: 'route',
                  legend: 'Route',
                  value: scope.route,
                  defaultValue: '',
                  choices: [{ value: '', label: 'All routes' }, ...options.routes],
                },
              ]
            : []),
          ...(options.customers.length
            ? [
                {
                  id: 'cust',
                  legend: 'Customer',
                  value: scope.customer,
                  defaultValue: '',
                  choices: [{ value: '', label: 'All customers' }, ...options.customers],
                },
              ]
            : []),
        ];
  const scopeCount = [scope.cls !== 'all', !!scope.site, !!scope.route, !!scope.customer].filter(Boolean).length;
  const phoneFilter = (
    <PhoneFilterSheet
      title="Filter the board"
      label="Show"
      value={
        view === 'run'
          ? 'Runs'
          : view === 'control'
            ? `Control room · ${scoped.length}`
            : `${tile ? `${tileLabel} · ${tileCounts[tile] ?? 0}` : `All · ${searched.length}`} · ${PHONE_VIEWS.find((v) => v.value === view)?.label}`
      }
      activeCount={(tile && view !== 'control' ? 1 : 0) + scopeCount}
      groups={[
        ...(view === 'control'
          ? []
          : [
              {
                id: 'f',
                legend: 'Show',
                value: tile,
                defaultValue: '',
                choices: [
                  { value: '', label: 'All trucks', count: view === 'run' ? null : searched.length },
                  ...(roomLabel ? [{ value: tile, label: roomLabel, count: tileCounts[tile] ?? 0 }] : []),
                  ...tiles.map((t) => ({
                    value: t.id,
                    label: t.label,
                    swatch: swatch(t),
                    count: view === 'run' ? null : tileCounts[t.id] ?? 0,
                  })),
                ],
              },
            ]),
        {
          id: 'view',
          legend: 'View',
          value: view,
          defaultValue: DEFAULT_VIEW,
          choices: PHONE_VIEWS.map((v) => ({ value: v.value, label: v.label, hint: v.hint })),
        },
        ...scopeGroups,
      ]}
      onApply={(v) =>
        setParams({
          f: v.view === 'control' ? '' : v.f ?? tile,
          view: v.view ? viewParam(v.view as View) : '',
          cls: v.cls ?? (scope.cls === 'all' ? '' : scope.cls),
          site: v.site ?? scope.site,
          route: v.route ?? scope.route,
          cust: v.cust ?? scope.customer,
        })
      }
    />
  );

  const header = (
    <PageHeader
      icon={FaTruckMoving}
      title="Status Board"
      subtitle="What every truck is doing, how far along its route it is, and fuel against its allowance."
      toolbar={
        canView ? (
          phone ? (
            phoneFilter
          ) : (
            <SegmentedControl<View>
              options={VIEWS}
              value={view}
              onChange={(v) => setParam('view', viewParam(v))}
              ariaLabel="Board view"
            />
          )
        ) : null
      }
      actions={
        canView ? (
          <div className="flex items-center gap-3">
            <ConnectionIndicator status={connection} compact={phone} />
            <button
              onClick={() => fetchData()}
              className={`${phone ? 'touch-target justify-center ' : ''}inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all`}
              aria-label="Refresh"
            >
              <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to view the status board."
      />
    );
  }

  const runDrawerNode = (
    <RunDrawer
      isOpen={!!runDrawer}
      runId={(runDrawer as (RunSummary & { id?: string }) | null)?.id}
      onClose={() => setRunDrawer(null)}
      renderNextLoad={
        canOverride
          ? (summary: RunGroup, reload: () => void) =>
              summary.run?.state === 'closed' ? null : (
                <NextLoadPanel
                  summary={summary}
                  onChanged={(msg) => {
                    setNotice(msg);
                    reload();
                    fetchData({ quiet: true });
                  }}
                />
              )
          : undefined
      }
    />
  );
  const overrideNode = (
    <OverrideDialog
      isOpen={!!overrideRow}
      row={overrideRow}
      onClose={() => setOverrideRow(null)}
      onDone={(msg: string) => {
        setNotice(msg);
        fetchData({ quiet: true });
      }}
    />
  );

  // ST-00249: the chips' maintenance menu and its dialog (the board refreshes after a booking).
  const onTruckAction: OnTruckAction | undefined = canMaintain
    ? (row, action) => {
        if (row.asset_id) setQuick({ mode: action, assetId: row.asset_id });
      }
    : undefined;
  const quickNode = quick ? (
    <QuickBooking
      mode={quick.mode}
      assetId={quick.assetId}
      assets={rows
        .filter((r) => r.asset_id)
        .map((r) => ({ id: r.asset_id as string, registration: r.registration ?? null, fleet_no: r.fleet_no ?? null, tenant_id: r.tenant_id ?? null }))}
      onClose={() => setQuick(null)}
      onDone={(_e, message) => {
        setNotice(message);
        fetchData({ quiet: true });
      }}
    />
  ) : null;

  if (phone) {
    const cardProps = {
      rows: sorted,
      now,
      canOverride,
      onOverride: setOverrideRow,
      onOpenRun: (run: RunSummary) => setRunDrawer(run),
      highlighted,
    };
    return (
      <div className="min-h-screen flex flex-col bg-slate-50" data-phone-layout="">
        {header}
        <div className="flex-1 pt-4 pb-20 space-y-3">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
          <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
          {view !== 'run' && view !== 'control' && (
            <div className="relative">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(e) => setParam('q', e.target.value)}
                placeholder="Registration, driver, trip, site..."
                aria-label="Search the board"
                enterKeyHint="search"
                className="w-full h-11 pl-10 pr-4 border border-slate-300 rounded-xl text-base bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 px-3 text-sm text-slate-600" role="status">
            {view !== 'control' && (
              <span>
                {view === 'run'
                  ? `${runs.length} run${runs.length === 1 ? '' : 's'} today`
                  : `${filtered.length} truck${filtered.length === 1 ? '' : 's'}`}
              </span>
            )}
            {view !== 'run' && <ScopeChip scope={scope} options={options} onClear={() => setScope(EMPTY_SCOPE)} />}
            {tile && view !== 'run' && view !== 'control' && (
              <button
                type="button"
                onClick={() => setParam('f', '')}
                className="touch-target inline-flex items-center gap-1.5 px-3 rounded-full border border-primary-400 bg-surface text-slate-800 font-medium"
                aria-label={`Showing ${tileLabel}. Show all trucks`}
              >
                {tileLabel}
                <FaTimes size={10} aria-hidden="true" />
              </button>
            )}
          </div>

          {view === 'control' ? (
            <ControlRoom
              rows={scoped}
              now={now}
              scope={scope}
              options={options}
              onScopeChange={setScope}
              onOpenRow={openControlRow}
              onOpenGroup={openControlGroup}
              fetchedAt={fetchedAt}
              error={!!error}
              connection={connection}
              loading={loading}
              phone
              onTruckAction={onTruckAction}
            />
          ) : loading && rows.length === 0 && runs.length === 0 ? (
            <LoadingState label="Loading the status board..." />
          ) : view === 'run' ? (
            runs.length === 0 ? (
              <div className={cardClasses}>
                <EmptyState icon={FaStream} title="No runs today">
                  Runs appear here once trucks have chained trips for the day.
                </EmptyState>
              </div>
            ) : (
              <div className="space-y-4 phone-runs">
                {runs.map((s) => (
                  <section
                    key={s.run?.id}
                    className={`${cardClasses} p-3`}
                    aria-label={`Run ${s.run?.registration || rowRegistration(s.run || {})}`}
                  >
                    <h3 className="text-sm font-semibold text-slate-900 mb-2">
                      <span className="font-mono">{s.run?.registration || rowRegistration(s.run || {})}</span>
                      <span className="text-slate-500 font-normal"> · {s.run?.driver_name || 'No driver recorded'}</span>
                    </h3>
                    <RunStrip summary={s} />
                  </section>
                ))}
              </div>
            )
          ) : filtered.length === 0 ? (
            <div className={cardClasses}>
              <EmptyState icon={FaTruckMoving} title={rows.length === 0 ? 'No assets to show' : 'No assets match'}>
                {rows.length === 0 ? (
                  'Trucks your organisation owns, or that serve your sites, appear here once they report.'
                ) : (
                  <button
                    type="button"
                    className="touch-target text-primary-700 font-medium hover:underline"
                    onClick={() => setParams({ f: '', q: '' })}
                  >
                    Clear the filters
                  </button>
                )}
              </EmptyState>
            </div>
          ) : view === 'kanban' ? (
            <PhoneStatusGroups {...cardProps} />
          ) : (
            <PhoneStatusCards {...cardProps} />
          )}
        </div>
        <div className="phone-targets">{overrideNode}</div>
        <div className="phone-targets">{quickNode}</div>
        <div className="phone-targets phone-runs">{runDrawerNode}</div>
      </div>
    );
  }

  if (view === 'control') {
    const room = (
      <ControlRoom
        rows={scoped}
        now={now}
        scope={scope}
        options={options}
        onScopeChange={setScope}
        onOpenRow={openControlRow}
        onOpenGroup={openControlGroup}
        fetchedAt={fetchedAt}
        error={!!error}
        connection={connection}
        loading={loading}
        wall={wall}
        wallTheme={wallTheme}
        onWallThemeChange={(t) => setParam('theme', t === 'dark' ? '' : t)}
        onEnterWall={enterWall}
        onExitWall={exitWall}
        onTruckAction={onTruckAction}
      />
    );
    if (wall) return room;
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-6">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
          <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
          {room}
        </div>
        {quickNode}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-6">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />

        {view !== 'run' && (
          <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Status filters">
            <ScopeChip scope={scope} options={options} onClear={() => setScope(EMPTY_SCOPE)} />
            {roomLabel && (
              <button
                type="button"
                onClick={() => setParam('f', '')}
                aria-pressed="true"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium bg-surface border-primary-400 text-slate-900 shadow-sm"
                aria-label={`Showing ${roomLabel} from the control room. Show all trucks`}
              >
                <FaTv size={11} aria-hidden="true" />
                {roomLabel}
                <span className="tabular-nums font-bold">{tileCounts[tile] ?? 0}</span>
                <FaTimes size={10} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setParam('f', '')}
              aria-pressed={!tile}
              className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${!tile ? 'bg-surface border-primary-400 text-slate-900 shadow-sm' : 'bg-surface border-slate-200 text-slate-600 hover:border-slate-300'}`}
            >
              All <span className="ml-1 tabular-nums font-bold">{searched.length}</span>
            </button>
            {tiles.map((t) => {
              const active = tile === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setParam('f', active ? '' : t.id)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${active ? 'bg-surface border-primary-400 text-slate-900 shadow-sm' : 'bg-surface border-slate-200 text-slate-600 hover:border-slate-300'}`}
                >
                  {t.tone ? (
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${ASSET_STATUS_CLASSES[t.tone].dot}`}
                      aria-hidden="true"
                    />
                  ) : (
                    <span aria-hidden="true">{t.glyph}</span>
                  )}
                  {t.label}
                  <span className="tabular-nums font-bold text-slate-900">{tileCounts[t.id]}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className={cardClasses}>
          <div className="px-6 py-4 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <p className="text-sm text-slate-600">
              {view === 'run'
                ? `${runs.length} run${runs.length === 1 ? '' : 's'} today`
                : `${filtered.length} asset${filtered.length === 1 ? '' : 's'}`}
              {selected.size > 0 && view !== 'run' && ` · ${selected.size} selected`}
            </p>
            {view !== 'run' && (
              <div className="flex flex-wrap items-center gap-2">
                {selected.size > 0 && (
                  <>
                    <button
                      type="button"
                      disabled
                      title="Messaging drivers from the board arrives with the conversation engine (M3)"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-400 bg-surface border border-slate-200 rounded-lg cursor-not-allowed"
                    >
                      <FaCommentDots size={12} /> Message
                    </button>
                    <button
                      type="button"
                      onClick={exportSelected}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                    >
                      <FaFileExport size={12} /> Export CSV
                    </button>
                  </>
                )}
                {view === 'table' && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setChooserOpen((o) => !o)}
                      aria-expanded={chooserOpen}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                    >
                      <FaColumns size={12} /> Columns
                    </button>
                    {chooserOpen && (
                      <div
                        role="group"
                        aria-label="Choose columns"
                        className="absolute right-0 mt-2 z-20 w-56 p-3 bg-surface border border-slate-200 rounded-lg shadow-lg space-y-1"
                      >
                        {availableColumns.map((c) => (
                          <label
                            key={c.id}
                            className="flex items-center gap-2 text-sm text-slate-700"
                          >
                            <input
                              type="checkbox"
                              checked={visibleIds.includes(c.id)}
                              disabled={c.locked}
                              onChange={() => toggleColumn(c.id)}
                              className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                            />
                            {c.label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="relative">
                  <FaSearch
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={14}
                  />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setParam('q', e.target.value)}
                    placeholder="Registration, driver, trip, site..."
                    aria-label="Search the board"
                    className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-64"
                  />
                </div>
              </div>
            )}
          </div>

          <div className={view === 'table' ? '' : 'p-4 sm:p-6'}>
            {loading && rows.length === 0 && runs.length === 0 ? (
              <LoadingState label="Loading the status board..." />
            ) : view === 'run' ? (
              runs.length === 0 ? (
                <EmptyState icon={FaStream} title="No runs today">
                  Runs appear here once trucks have chained trips for the day.
                </EmptyState>
              ) : (
                <div className="space-y-6">
                  {runs.map((s) => (
                    <section
                      key={s.run?.id}
                      aria-label={`Run ${s.run?.registration || rowRegistration(s.run || {})}`}
                    >
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">
                        <span className="font-mono">
                          {s.run?.registration || rowRegistration(s.run || {})}
                        </span>
                        <span className="text-slate-500 font-normal">
                          {' '}
                          · {s.run?.driver_name || 'No driver recorded'}
                        </span>
                      </h3>
                      <RunStrip summary={s} />
                    </section>
                  ))}
                </div>
              )
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={FaTruckMoving}
                title={rows.length === 0 ? 'No assets to show' : 'No assets match'}
              >
                {rows.length === 0 ? (
                  'Trucks your organisation owns, or that serve your sites, appear here once they report.'
                ) : (
                  <button
                    type="button"
                    className="text-primary-700 font-medium hover:underline"
                    onClick={() => setParams({ f: '', q: '' })}
                  >
                    Clear the filters
                  </button>
                )}
              </EmptyState>
            ) : view === 'kanban' ? (
              <KanbanBoard
                rows={sorted}
                onOpenTrip={openTrip}
                onOpenRun={(run) => setRunDrawer(run)}
                now={now}
              />
            ) : (
              <StatusTable
                rows={sorted}
                visible={visibleIds}
                sort={sort}
                onSort={onSort}
                selected={selected}
                onToggle={toggleRow}
                onToggleAll={toggleAll}
                onOpenTrip={openTrip}
                onOpenRun={(run: RunSummary) => setRunDrawer(run)}
                onOverride={setOverrideRow}
                canOverride={canOverride}
                highlighted={highlighted}
                now={now}
              />
            )}
          </div>
        </div>
      </div>

      {overrideNode}

      {runDrawerNode}
    </div>
  );
};

export default StatusBoard;
